// Sopwith 3D - simulation types.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.

import type { Game } from './game';

export enum ObjType {
  GROUND = 0,
  PLANE,
  BOMB,
  SHOT,
  TARGET,
  EXPLOSION,
  SMOKE,
  FLOCK,
  BIRD,
  OX,
  MISSILE,
  STARBURST,
  BALLOON,
  POWERUP,
  DUMMYTYPE = 99,
}

export const OBJ_TYPE_NAMES: readonly string[] = [
  'GROUND',
  'PLANE',
  'BOMB',
  'SHOT',
  'TARGET',
  'EXPLOSION',
  'SMOKE',
  'FLOCK',
  'BIRD',
  'OX',
  'MISSILE',
  'STARBURST',
  'BALLOON',
  'POWERUP',
];

export enum ObjState {
  WAITING = 0,
  FLYING,
  HIT,
  CRASHED,
  FALLING,
  STANDING,
  STALLED,
  REBUILDING,
  WOUNDED,
  WOUNDSTALL,
  FINISHED = 91,
}

export enum EndStatus {
  PLAYING = 0,
  WINNER,
  LOSER,
}

export enum PlayMode {
  UNSET = 0,
  SINGLE,
  COMPUTER,
  ASYNCH,
  NOVICE,
}

export enum Faction {
  NONE = 0,
  PLAYER1,
  PLAYER2,
  PLAYER3,
  PLAYER4,
  PLAYER5,
  PLAYER6,
  PLAYER7,
  PLAYER8,
  NUM_FACTIONS,
}

export const FACTION_NAMES: readonly string[] = [
  'NONE',
  'PLAYER1',
  'PLAYER2',
  'PLAYER3',
  'PLAYER4',
  'PLAYER5',
  'PLAYER6',
  'PLAYER7',
  'PLAYER8',
];

export enum Transform {
  NONE = 0,
  ROTATE90,
  ROTATE180,
  ROTATE270,
  FLIP,
  FLIP_ROTATE90,
  MIRROR,
  MIRROR_ROTATE90,
}

export const TRANSFORM_NAMES: readonly string[] = [
  'NONE',
  'ROTATE90',
  'ROTATE180',
  'ROTATE270',
  'FLIP',
  'FLIP_ROTATE90',
  'MIRROR',
  'MIRROR_ROTATE90',
];

export enum TargetType {
  HANGAR = 0,
  FACTORY,
  OIL_TANK,
  TANK,
  TRUCK,
  TANKER_TRUCK,
  FLAG,
  TENT,
  CUSTOM1,
  CUSTOM2,
  CUSTOM3,
  CUSTOM4,
  CUSTOM5,
  CUSTOM_PASSIVE1,
  CUSTOM_PASSIVE2,
  CUSTOM_PASSIVE3,
  CUSTOM_PASSIVE4,
  CUSTOM_PASSIVE5,
  RADIO_TOWER,
  WATER_TOWER,
}

export enum PowerupType {
  AMMO = 0,
  BOMB,
  FUEL,
  AMMO_BIG,
  BOMB_BIG,
  FUEL_BIG,
}

/** A decoded sprite: `data[y * w + x]` is a colour index 0..3, 0 = transparent. */
export interface Sopsym {
  w: number;
  h: number;
  data: Uint8Array;
}

/** A sprite in all eight rotation/mirror transforms (see Transform). */
export interface Symset {
  sym: Sopsym[];
}

/** An object as defined in the level data. */
export interface OriginalOb {
  type: ObjType;
  x: number;
  orient: number;
  territoryL: number;
  territoryR: number;
  faction: Faction;
  transform: Transform;
}

/** A level definition: terrain heights plus the objects placed on it. */
export interface LevelData {
  /** Length of `ground`; the world is this many units wide. */
  maxX: number;
  ground: Uint8Array;
  objects: OriginalOb[];
}

export interface Score {
  score: number;
  medals: number[];
  ribbons: number[];
  /** Banked on safe landings. */
  planekills: number;
  landings: number;
  valour: number;
}

export function newScore(): Score {
  return { score: 0, medals: [], ribbons: [], planekills: 0, landings: 0, valour: 0 };
}

export interface FlightScore {
  planekills: number;
  killscore: number;
  valour: number;
  combatwound: boolean;
}

export type MoveFn = (g: Game, ob: Obj) => boolean;

/**
 * A live simulation object. Field names follow the original `OBJECTS` struct
 * (minus the `ob_` prefix) so the port can be checked against the C source.
 */
export class Obj {
  state = ObjState.WAITING;
  x = 0;
  y = 0;
  dx = 0;
  dy = 0;
  angle = 0;
  orient = 0;
  speed = 0;
  accel = 0;
  flaps = 0;
  firing: Obj | null = null;
  rounds = 0;
  hitcount = 0;
  life = 0;
  owner: Obj | null = null;
  faction = Faction.NONE;
  target: Obj | null = null;
  bombs = 0;
  clr = 0;
  /** Fractional (low 16 bits) parts of x, y, dx, dy. */
  lx = 0;
  ly = 0;
  ldx = 0;
  ldy = 0;
  /** Object list links (creation order). */
  next: Obj | null = null;
  prev: Obj | null = null;
  movef: MoveFn | null = null;
  /** X-sorted list links (used by collision detection). */
  xnext: Obj | null = null;
  xprev: Obj | null = null;
  crashcnt = 0;
  symbol: Sopsym | null = null;
  bdelay = 0;
  type = ObjType.DUMMYTYPE;
  missiles = 0;
  mfiring: Obj | null = null;
  mdelay = 0;
  missiletarget: Obj | null = null;
  bursts = 0;
  bsdelay = 0;
  plrnum = 0;
  endsts = EndStatus.PLAYING;
  score: Score = newScore();
  flightscore: FlightScore = { planekills: 0, killscore: 0, valour: 0, combatwound: false };
  originalOb: OriginalOb | null = null;
  /** Home Y, generated when the object is first placed (X lives in originalOb). */
  origY = 0;
  bombing = false;
  drwflg = false;
  onmap = false;
  home = false;
  athome = false;
  bfiring = false;
  goingsun = false;
  /** Stable index, handy for renderers that track objects across ticks. */
  id = 0;
  /**
   * Port additions for the screen-relative pitch option: which pitch key was
   * held last tick, and the turn direction latched when it was pressed
   * (+1 anticlockwise, -1 clockwise).
   */
  pitchHeld = 0;
  pitchLatch = 0;
}
